import { Action, ActionList, OverflowAction, useCan } from '@if/mui'
import { access } from '@src/access'
import { useGetRefundableProducts } from '@src/api/loan/loan-api'
import { useLoanLock } from '@src/providers/LoanLockProvider'
import { Banknote, CalendarRange, FileX, History, Plus, Repeat, Timer, Undo2 } from 'lucide-react'

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ELoanCalendarAction, useLoanCalendarAction } from '../LoanCalendarActionProvider'
import { useLoanCalendarSimulation } from '../LoanCalendarSimulationProvider'

const LoanCalendarTablePrimaryActions = () => {
  const { t } = useTranslation()
  const { dispatchAction } = useLoanCalendarAction()
  const { currentSimulation } = useLoanCalendarSimulation()
  const canPerformChargeOff = useCan(access.performChargeOff)
  const canPerformProductRefund = useCan(access.performProductRefund)
  const { isReadOnly } = useLoanLock()
  // Same query the refund dialog reads, so the entry greys out without a second request.
  // Only a list known to be empty greys it out: after a failed fetch the dialog shows the error instead.
  const { loan } = currentSimulation.simulation.fullLoan
  const { data: refundableProducts = [], isSuccess: refundableProductsLoaded } = useGetRefundableProducts(
    loan.id,
    loan.financingCompanyId,
  )
  const hasNoRefundableProduct = refundableProductsLoaded && refundableProducts.length === 0

  const actions = useMemo<Action[]>(
    () => [
      {
        icon: Plus,
        label: t('loan.payment.add'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenAddPaymentDialog),
      },
      {
        icon: Plus,
        label: t('loan.payment.addFee'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenAddFeeDialog),
      },
      {
        icon: History,
        label: t('loan.payment.reset'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenResetPaymentCalendarDialog),
      },
    ],
    [t, dispatchAction, isReadOnly],
  )

  const overflowActions = useMemo<OverflowAction[]>(
    () => [
      {
        icon: CalendarRange,
        label: t('loan.payment.moveCalendar'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenMoveCalendarDialog),
      },
      {
        icon: Timer,
        label: t('loan.payment.editPaymentFrequency.title'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenUpdatePaymentFrequencyDialog),
      },
      {
        icon: Banknote,
        label: t('loan.payment.editRegularPayment'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenChangeRegularPmtDialog),
      },
      {
        icon: Repeat,
        label: t('loan.payment.addRecurringPayments.title'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenAddRecurringPaymentsDialog),
      },
      ...(canPerformChargeOff
        ? [
            {
              icon: FileX,
              label: t('loan.payment.chargeOff'),
              disabled: isReadOnly,
              onClick: () => dispatchAction(ELoanCalendarAction.OpenChargeOffDialog),
            } satisfies OverflowAction,
          ]
        : []),
      ...(canPerformProductRefund
        ? [
            {
              icon: Undo2,
              label: t('loan.payment.productRefund'),
              // Nothing to refund on this loan: the dialog would only refuse the entry.
              disabled: isReadOnly || hasNoRefundableProduct,
              onClick: () => dispatchAction(ELoanCalendarAction.OpenProductRefundDialog),
            } satisfies OverflowAction,
          ]
        : []),
    ],
    [t, dispatchAction, canPerformChargeOff, isReadOnly, canPerformProductRefund, hasNoRefundableProduct],
  )

  return (
    <ActionList
      variant="button"
      size="small"
      menuBelow="md"
      actions={actions}
      overflowActions={overflowActions}
      overflowLabel={t('loan.payment.otherGlobalActions')}
    />
  )
}

export default LoanCalendarTablePrimaryActions
